import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Summarize this patient's recent vitals and flag any concerns.",
  "Generate a follow-up plan for a hypertensive patient on lisinopril.",
  "Differential diagnoses for acute lower abdominal pain in a 30-year-old female.",
  "Draft a brief patient discharge summary for post-op appendectomy.",
];

export default function AIPage() {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs]);

  const send = async (msg) => {
    if (!msg.trim()) return;
    setMsgs((m) => [...m, { role: "user", content: msg }]);
    setText(""); setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      toast.error("AI request failed");
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry, I couldn't process that. Please try again." }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5 fade-up h-[calc(100vh-9rem)] flex flex-col" data-testid="ai-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Sparkles className="size-7 text-accent" /> Senrize Medical Flow AI
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Claude Sonnet 4.5 · medical reasoning support · not a substitute for clinical judgement.</p>
      </div>

      <Card className="flex-1 flex flex-col overflow-hidden">
        <ScrollArea className="flex-1" ref={scrollRef}>
          <div className="p-6 space-y-5">
            {msgs.length === 0 && (
              <div className="space-y-4">
                <div className="text-center py-8">
                  <div className="size-14 mx-auto rounded-full bg-accent/15 text-accent grid place-items-center mb-3">
                    <Sparkles className="size-6" />
                  </div>
                  <div className="text-lg font-semibold">How can I help today?</div>
                  <div className="text-sm text-muted-foreground">Ask about diagnoses, treatments, EMR summaries.</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)}
                      className="text-left text-sm p-3 border border-border rounded-lg hover:bg-muted/60 transition-colors"
                      data-testid={`ai-suggestion-${SUGGESTIONS.indexOf(s)}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={`msg-${i}-${m.role}`} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <Avatar className="size-8 shrink-0">
                  <AvatarFallback className={m.role === "user" ? "bg-primary text-primary-foreground text-xs" : "bg-accent text-accent-foreground text-xs"}>
                    {m.role === "user" ? user.name?.slice(0,2).toUpperCase() : "AI"}
                  </AvatarFallback>
                </Avatar>
                <div className={`max-w-[78%] p-3.5 rounded-lg text-sm leading-relaxed whitespace-pre-line ${
                  m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <Avatar className="size-8"><AvatarFallback className="bg-accent text-accent-foreground text-xs">AI</AvatarFallback></Avatar>
                <div className="p-3.5 rounded-lg bg-muted text-sm flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" /> Thinking...
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <div className="border-t border-border p-4">
          <form onSubmit={(e) => { e.preventDefault(); send(text); }} className="flex gap-2">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(text); } }}
              placeholder="Ask Senrize AI..."
              rows={1}
              className="resize-none"
              data-testid="ai-input"
            />
            <Button type="submit" disabled={loading || !text.trim()} className="px-4" data-testid="ai-send">
              {loading ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
